import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const DESKTOP_MQ = '(min-width: 64rem)';
const IDLE_TIMEOUT_MS = 2000;

/**
 * @param {Function} callback
 * @param {number} [timeout]
 */
function scheduleIdle(callback, timeout = IDLE_TIMEOUT_MS) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(callback, { timeout });
  } else {
    setTimeout(callback, 1);
  }
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.dataset.deferGallery === 'true') {
      this.#initDeferredGallery({ signal });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @param {{ signal: AbortSignal }} options
   */
  #initDeferredGallery({ signal }) {
    const desktopQuery = window.matchMedia(DESKTOP_MQ);

    if (!desktopQuery.matches) {
      this.#scheduleSlideshowHydration();
    }

    desktopQuery.addEventListener(
      'change',
      (event) => {
        if (event.matches) {
          this.#hydrateDeferredGrid();
        } else {
          this.#scheduleSlideshowHydration();
        }
      },
      { signal }
    );
  }

  #scheduleSlideshowHydration() {
    if (this.dataset.slideshowSlidesHydrated === 'true') {
      return;
    }

    const hydrate = () => {
      this.#hydrateDeferredSlideshowSlides();
    };

    const captureOptions = {
      once: true,
      passive: true,
      capture: true,
      signal: this.#controller.signal,
    };

    // Capture phase so slides exist before slideshow pointerdown / touch handling.
    this.addEventListener('pointerdown', hydrate, captureOptions);
    this.addEventListener('touchstart', hydrate, captureOptions);
    scheduleIdle(hydrate, IDLE_TIMEOUT_MS);
  }

  #hydrateDeferredSlideshowSlides() {
    if (this.dataset.slideshowSlidesHydrated === 'true') {
      return;
    }

    const template = this.querySelector('template[data-deferred-slideshow-slides]');
    const scroller = this.querySelector('slideshow-slides');

    if (!template || !scroller) {
      return;
    }

    scroller.appendChild(template.content.cloneNode(true));
    template.remove();
    this.dataset.slideshowSlidesHydrated = 'true';

    const slideshow = this.querySelector('slideshow-component');
    slideshow?.refreshSlides?.();

    this.dispatchEvent(new CustomEvent('media-gallery:deferred-hydrated', { bubbles: true }));
  }

  #hydrateDeferredGrid() {
    if (this.dataset.gridHydrated === 'true') {
      return;
    }

    const template = this.querySelector('template[data-deferred-grid]');

    if (!template) {
      return;
    }

    template.content.querySelectorAll('[data-deferred-grid-content]').forEach((wrapper) => {
      const index = wrapper.getAttribute('data-deferred-grid-content');
      const shell = this.querySelector(`[data-deferred-grid-item="${index}"]`);

      if (!shell) {
        return;
      }

      while (wrapper.firstChild) {
        shell.appendChild(wrapper.firstChild);
      }
    });

    template.remove();
    this.dataset.gridHydrated = 'true';

    this.dispatchEvent(new CustomEvent('media-gallery:deferred-hydrated', { bubbles: true }));
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
